import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  listAdminWaitlist,
  type WaitlistEntry,
  type WaitlistFilter,
  type WaitlistListResponse,
} from '../lib/admin';

const PAGE_SIZE = 50;
const LOCALES = ['en', 'de', 'fr', 'it'];

export function AdminWaitlist() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [source, setSource] = useState('');
  const [locale, setLocale] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<WaitlistListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [detail, setDetail] = useState<WaitlistEntry | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setOffset(0), [debouncedSearch, source, locale]);

  const filter: WaitlistFilter = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      source: source || undefined,
      locale: locale || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [debouncedSearch, source, locale, offset],
  );

  async function load() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      setData(await listAdminWaitlist(accessToken, filter));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filter.search, filter.source, filter.locale, filter.offset]);

  function clearFilters() {
    setSearch('');
    setSource('');
    setLocale('');
  }

  function exportCsv() {
    if (!data || data.entries.length === 0) return;
    const header = ['email', 'source', 'locale', 'created_at', 'ip', 'referer'];
    const rows = data.entries.map((e) => [
      e.email,
      e.source,
      e.locale,
      e.createdAt,
      e.ip ?? '',
      e.referer ?? '',
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const total = data?.total ?? 0;
  const showing = data?.entries.length ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + showing;
  const sources = data?.sources ?? [];

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Waitlist</h1>
            <div class="admin-who" style={{ marginLeft: 'auto' }}>
              <span class="admin-mono">{total} total</span>
            </div>
          </header>

          <p class="admin-lede">
            Everyone who left their email on the landing page or store modal.
            Honeypot submissions are dropped before they reach this table.
            CSV export reflects the current filter + page.
          </p>

          <div class="admin-filter-row">
            <input
              class="admin-filter-input"
              type="search"
              placeholder="Search email…"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />
            <select
              class="admin-filter-select"
              value={source}
              onChange={(e) => setSource((e.target as HTMLSelectElement).value)}
            >
              <option value="">All sources</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              class="admin-filter-select"
              value={locale}
              onChange={(e) => setLocale((e.target as HTMLSelectElement).value)}
            >
              <option value="">All locales</option>
              {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <button type="button" class="admin-link" onClick={clearFilters}>Reset</button>
            <button
              type="button"
              class="admin-link"
              onClick={exportCsv}
              disabled={!data || data.entries.length === 0}
            >
              Export CSV
            </button>
          </div>

          {err && <p class="admin-error">{err}</p>}
          {loading && !data && <p class="admin-lede">Loading…</p>}

          {data && data.entries.length === 0 && !loading && (
            <p class="admin-lede">No waitlist entries match these filters.</p>
          )}

          {data && data.entries.length > 0 && (
            <>
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Source</th>
                    <th>Locale</th>
                    <th>IP</th>
                    <th>Signed up</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id}>
                      <td>{e.email}</td>
                      <td>
                        {e.source ? (
                          <span class="admin-mono" style={{ fontSize: 11 }}>{e.source}</span>
                        ) : (
                          <span style={{ opacity: 0.4 }}>—</span>
                        )}
                      </td>
                      <td class="admin-mono">{e.locale}</td>
                      <td class="admin-mono" style={{ fontSize: 11 }}>
                        {e.ip || <span style={{ opacity: 0.4 }}>—</span>}
                      </td>
                      <td class="admin-mono">{fmtDate(e.createdAt)}</td>
                      <td>
                        <button class="admin-link" onClick={() => setDetail(e)}>Details</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div class="admin-pager">
                <span class="admin-mono">{from}–{to} of {total}</span>
                <div class="admin-pager-buttons">
                  <button
                    class="admin-link"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    ← Prev
                  </button>
                  <button
                    class="admin-link"
                    disabled={to >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {detail && (
          <DetailModal entry={detail} onClose={() => setDetail(null)} />
        )}
      </div>
    </RequireAdmin>
  );
}

function DetailModal({ entry, onClose }: { entry: WaitlistEntry; onClose: () => void }) {
  return (
    <div class="admin-overlay" onClick={onClose}>
      <div
        class="admin-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)' }}
      >
        <h2>{entry.email}</h2>
        <p class="admin-lede" style={{ marginBottom: 12 }}>
          Signed up {fmtDate(entry.createdAt)}
        </p>
        <dl class="admin-meta">
          <dt>Source</dt>
          <dd>{entry.source || <span style={{ opacity: 0.4 }}>—</span>}</dd>
          <dt>Locale</dt>
          <dd class="admin-mono">{entry.locale}</dd>
          <dt>IP</dt>
          <dd class="admin-mono" style={{ fontSize: 11 }}>
            {entry.ip || <span style={{ opacity: 0.4 }}>—</span>}
          </dd>
          <dt>User agent</dt>
          <dd class="admin-mono" style={{ fontSize: 11, wordBreak: 'break-all' }}>
            {entry.userAgent || <span style={{ opacity: 0.4 }}>—</span>}
          </dd>
          <dt>Referer</dt>
          <dd class="admin-mono" style={{ fontSize: 11, wordBreak: 'break-all' }}>
            {entry.referer || <span style={{ opacity: 0.4 }}>—</span>}
          </dd>
        </dl>
        <div class="admin-modal-actions">
          <button class="admin-link" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}
